import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '@/store';
import {
  fetchCart,
  updateCartItem,
  removeFromCart,
  clearCart,
  increaseCartItem,
  decreaseCartItem,
  saveForLater,
  moveToCart,
  fetchSavedForLater
} from '@/store/slices/cartSlice';
import { CartItem } from '@/types';
import { getPrimaryImage, getPlaceholderImage } from '@/utils/imageUtils';
import { formatCurrency } from '@/utils/formatters';
import Button from '@/components/ui/Button';
import { TrashIcon, MinusIcon, PlusIcon, ShoppingBagIcon, ExclamationTriangleIcon, BookmarkIcon, ArrowLeftIcon, TruckIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const CartPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cartState = useSelector((state: RootState) => state.cart);
  const { items = [], savedItems = [], totalItems = 0, totalAmount = 0, isLoading = false, isLoadingSaved = false, error = null } = cartState || {};
  const [showSavedItems, setShowSavedItems] = useState(false);

  useEffect(() => {
    try {
      dispatch(fetchCart());
      dispatch(fetchSavedForLater());
    } catch (err) {
      console.error('Error fetching cart:', err);
    }
  }, [dispatch]);

  const handleUpdateQuantity = async (item: CartItem, newQuantity: number) => {
    if (newQuantity <= 0) {
      if (window.confirm('Remove this item from cart?')) {
        try {
          await dispatch(removeFromCart(item.id)).unwrap();
          dispatch(fetchCart()); // Refresh cart after removal
          toast.success('Item removed from cart');
        } catch (error: any) {
          toast.error(error || 'Failed to remove item');
        }
      }
    } else {
      const maxQuantity = item.product?.inventory?.current_stock || 999;
      if (newQuantity > maxQuantity) {
        toast.error(`Only ${maxQuantity} items available in stock`);
        return;
      }
      try {
        await dispatch(updateCartItem({ itemId: item.id, quantity: newQuantity })).unwrap();
        // State is updated directly in the reducer, no need to refetch
        toast.success('Quantity updated');
      } catch (error: any) {
        toast.error(error || 'Failed to update quantity');
      }
    }
  };

  const handleIncreaseQuantity = async (item: CartItem) => {
    try {
      // Check stock availability
      const maxQuantity = item.product?.inventory?.current_stock || 0;
      if (item.quantity >= maxQuantity) {
        toast.error(`Only ${maxQuantity} items available in stock`);
        return;
      }
      await dispatch(increaseCartItem(item.id)).unwrap();
      // State is updated directly in the reducer, no need to refetch
      toast.success('Quantity updated');
    } catch (error: any) {
      toast.error(error || 'Failed to update quantity');
    }
  };

  const handleDecreaseQuantity = async (item: CartItem) => {
    try {
      if (item.quantity <= 1) {
        if (window.confirm('Remove this item from cart?')) {
          await dispatch(removeFromCart(item.id)).unwrap();
          dispatch(fetchCart()); // Refresh cart after removal
          toast.success('Item removed from cart');
        }
      } else {
        await dispatch(decreaseCartItem(item.id)).unwrap();
        // State is updated directly in the reducer, no need to refetch
        toast.success('Quantity updated');
      }
    } catch (error: any) {
      toast.error(error || 'Failed to update quantity');
    }
  };

  const handleRemoveItem = async (itemId: number) => {
    if (window.confirm('Are you sure you want to remove this item from your cart?')) {
      try {
        await dispatch(removeFromCart(itemId)).unwrap();
        toast.success('Item removed from cart');
      } catch (error: any) {
        toast.error(error || 'Failed to remove item');
      }
    }
  };

  const handleClearCart = () => {
    if (window.confirm('Are you sure you want to clear your cart?')) {
      dispatch(clearCart());
    }
  };

  const handleCheckout = () => {
    navigate('/buyer/checkout');
  };

  const handleSaveForLater = async (itemId: number) => {
    try {
      await dispatch(saveForLater(itemId)).unwrap();
      // Refresh both cart and saved items
      dispatch(fetchCart());
      dispatch(fetchSavedForLater());
    } catch (error: any) {
      // Error already shown by the thunk
    }
  };

  const handleMoveToCart = async (itemId: number) => {
    try {
      await dispatch(moveToCart(itemId)).unwrap();
      // Refresh both cart and saved items
      dispatch(fetchCart());
      dispatch(fetchSavedForLater());
    } catch (error: any) {
      // Error already shown by the thunk
    }
  };

  // Safety check - ensure we always render something
  if (isLoading && items.length === 0 && !error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading cart...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && items.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-red-500 mb-4">
            <ExclamationTriangleIcon className="h-16 w-16 mx-auto" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Cart</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="flex gap-4 justify-center">
            <Button onClick={() => {
              dispatch(fetchCart());
              dispatch(fetchSavedForLater());
            }}>
              Retry
            </Button>
            <Button variant="outline" onClick={() => navigate('/products')}>
              Continue Shopping
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Shopping Cart</h1>
            <p className="mt-2 text-gray-600">
              {totalItems > 0 ? `${totalItems} item${totalItems > 1 ? 's' : ''} in your cart` : 'Your cart is empty'}
            </p>
          </div>
          {totalItems > 0 && (
            <Button 
              variant="outline" 
              onClick={handleClearCart}
              className="text-red-600 border-red-300 hover:bg-red-50"
            >
              <TrashIcon className="h-5 w-5 mr-2" />
              Clear Cart
            </Button>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md mb-6">
            {error}
          </div>
        )}

        {items.length === 0 ? (
          /* Empty Cart */
          <div className="text-center py-16">
            <div className="text-gray-400 mb-6">
              <ShoppingBagIcon className="h-32 w-32 mx-auto" />
            </div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-2">Your cart is empty</h3>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              Looks like you haven't added any items to your cart yet. Start shopping to add products!
            </p>
            <Button 
              onClick={() => navigate('/products')}
              className="px-8 py-3"
            >
              Start Shopping
            </Button>
          </div>
        ) : (
          /* Cart Items */
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items List */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow-sm border">
                <div className="p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Cart Items</h2>
                  
                  <div className="space-y-4">
                    {items.map((item) => {
                      // Get stable image URL - use product ID and first image ID as key
                      const imageUrl = getPrimaryImage(item.product?.images);
                      const placeholderUrl = getPlaceholderImage(80, 80, 'No Image');
                      const imageKey = item.product?.id ? `product-${item.product.id}-${item.product.images?.[0]?.id || 0}` : `item-${item.id}`;
                      
                      return (
                      <div key={item.id} className="flex items-center space-x-4 p-4 border border-gray-200 rounded-lg">
                        {/* Product Image */}
                        <div className="flex-shrink-0">
                          <img
                            key={imageKey}
                            src={imageUrl}
                            alt={item.product?.name || 'Product'}
                            className="w-20 h-20 object-cover rounded-md"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              // Only update if not already showing placeholder
                              if (!target.src.includes('via.placeholder.com')) {
                                target.src = placeholderUrl;
                              }
                            }}
                            loading="lazy"
                          />
                        </div>

                        {/* Product Info */}
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-medium text-gray-900 truncate">
                            {item.product?.name}
                          </h3>
                          {item.product?.brand && (
                            <p className="text-sm text-gray-500">{item.product.brand}</p>
                          )}
                          <div className="flex items-center space-x-2 mt-1 flex-wrap">
                            <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                              item.product?.product_type === 'brand_new'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-blue-100 text-blue-800'
                            }`}>
                              {item.product?.product_type === 'brand_new' ? 'Brand New' : 'Recycled'}
                            </span>
                            {/* Stock Availability */}
                            {item.product?.inventory && (
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                item.product.inventory.current_stock > 0
                                  ? item.product.inventory.current_stock >= item.quantity
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-amber-100 text-amber-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {item.product.inventory.current_stock > 0 ? (
                                  <>
                                    {item.product.inventory.current_stock >= item.quantity ? (
                                      `${item.product.inventory.current_stock} in stock`
                                    ) : (
                                      <>
                                        <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                                        Only {item.product.inventory.current_stock} left
                                      </>
                                    )}
                                  </>
                                ) : (
                                  'Out of stock'
                                )}
                              </span>
                            )}
                          </div>
                          {/* Delivery Info */}
                          {item.delivery_zone_name && (
                            <div className="flex items-center space-x-1 mt-2 text-xs text-gray-600">
                              <TruckIcon className="h-3.5 w-3.5" />
                              <span>{item.delivery_zone_name}:</span>
                              <span className={item.delivery_charge_at_time === 0 ? 'text-green-600 font-medium' : 'text-gray-900 font-medium'}>
                                {item.delivery_charge_at_time === 0 ? 'FREE' : formatCurrency(item.delivery_charge_at_time || 0)}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Quantity Controls */}
                        <div className="flex flex-col items-center space-y-1">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleDecreaseQuantity(item)}
                              className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
                              disabled={isLoading || item.quantity <= 1}
                              title="Decrease quantity"
                            >
                              <MinusIcon className="h-4 w-4" />
                            </button>
                            <input
                              type="number"
                              min="1"
                              max={item.product?.inventory?.current_stock || 999}
                              value={item.quantity}
                              onChange={(e) => {
                                const newQty = parseInt(e.target.value) || 1;
                                const maxQty = item.product?.inventory?.current_stock || 999;
                                if (newQty > 0 && newQty <= maxQty) {
                                  handleUpdateQuantity(item, newQty);
                                } else if (newQty > maxQty) {
                                  toast.error(`Only ${maxQty} items available`);
                                }
                              }}
                              className="w-16 text-center border border-gray-300 rounded-md py-1 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              disabled={isLoading}
                            />
                            <button
                              onClick={() => handleIncreaseQuantity(item)}
                              className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
                              disabled={
                                isLoading || 
                                item.quantity >= (item.product?.inventory?.current_stock || 0)
                              }
                              title="Increase quantity"
                            >
                              <PlusIcon className="h-4 w-4" />
                            </button>
                          </div>
                          {item.product?.inventory && item.quantity > item.product.inventory.current_stock && (
                            <span className="text-xs text-red-600">Max: {item.product.inventory.current_stock}</span>
                          )}
                        </div>

                        {/* Price */}
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900">
                            {formatCurrency((item.price_at_time || item.product?.price || 0) * item.quantity)}
                          </p>
                          <p className="text-sm text-gray-500">
                            {formatCurrency(item.price_at_time || item.product?.price || 0)} each
                          </p>
                        </div>

                        {/* Actions */}
                        <div className="flex flex-col space-y-2">
                          <button
                            onClick={() => handleSaveForLater(item.id)}
                            className="text-blue-600 hover:text-blue-800 p-1 flex items-center space-x-1 text-sm"
                            disabled={isLoading}
                            title="Save for later"
                          >
                            <BookmarkIcon className="h-4 w-4" />
                            <span className="hidden sm:inline">Save</span>
                          </button>
                          <button
                            onClick={() => handleRemoveItem(item.id)}
                            className="text-red-600 hover:text-red-800 p-1"
                            disabled={isLoading}
                            title="Remove item"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Saved for Later Section */}
              {savedItems.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm border mt-6">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                        <BookmarkIcon className="h-5 w-5 mr-2 text-blue-600" />
                        Saved for Later ({savedItems.length})
                      </h2>
                      <button
                        onClick={() => setShowSavedItems(!showSavedItems)}
                        className="text-sm text-primary-600 hover:text-primary-700"
                      >
                        {showSavedItems ? 'Hide' : 'Show'}
                      </button>
                    </div>

                    {showSavedItems && (
                      <div className="space-y-4">
                        {savedItems.map((item) => {
                          // Get stable image URL
                          const imageUrl = getPrimaryImage(item.product?.images);
                          const placeholderUrl = getPlaceholderImage(64, 64, 'No Image');
                          const imageKey = item.product?.id ? `saved-product-${item.product.id}-${item.product.images?.[0]?.id || 0}` : `saved-item-${item.id}`;
                          
                          return (
                          <div key={item.id} className="flex items-center space-x-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                            {/* Product Image */}
                            <div className="flex-shrink-0">
                              <img
                                key={imageKey}
                                src={imageUrl}
                                alt={item.product?.name || 'Product'}
                                className="w-16 h-16 object-cover rounded-md"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  // Only update if not already showing placeholder
                                  if (!target.src.includes('via.placeholder.com')) {
                                    target.src = placeholderUrl;
                                  }
                                }}
                                loading="lazy"
                              />
                            </div>

                            {/* Product Info */}
                            <div className="flex-1 min-w-0">
                              <h3 className="text-sm font-medium text-gray-900 truncate">
                                {item.product?.name}
                              </h3>
                              {item.product?.brand && (
                                <p className="text-sm text-gray-500">{item.product.brand}</p>
                              )}
                              <div className="flex items-center space-x-2 mt-1">
                                <span className="text-sm font-medium text-gray-900">
                                  {formatCurrency(item.price_at_time || item.product?.price || 0)} × {item.quantity}
                                </span>
                              </div>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center space-x-2">
                              <Button
                                size="sm"
                                onClick={() => handleMoveToCart(item.id)}
                                disabled={isLoading}
                                className="flex items-center space-x-1"
                              >
                                <ArrowLeftIcon className="h-4 w-4" />
                                <span>Move to Cart</span>
                              </Button>
                              <button
                                onClick={() => handleRemoveItem(item.id)}
                                className="text-red-600 hover:text-red-800 p-1"
                                disabled={isLoading}
                                title="Remove item"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-sm border p-6 sticky top-8">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h2>

                <div className="space-y-3">
                  {/* Products Total */}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Products ({totalItems})</span>
                    <span className="font-medium">
                      {formatCurrency(
                        items.reduce((total, item) => {
                          return total + (item.price_at_time || item.product?.price || 0) * item.quantity;
                        }, 0)
                      )}
                    </span>
                  </div>

                  {/* Delivery Charges - Calculated at checkout */}
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600 flex items-center">
                        <TruckIcon className="h-4 w-4 mr-1.5" />
                        Delivery Charges
                      </span>
                      <span className="text-sm text-gray-500 italic">
                        Calculated at checkout
                      </span>
                    </div>
                  </div>

                  {/* Total */}
                  <div className="border-t pt-3">
                    <div className="flex justify-between">
                      <span className="text-lg font-semibold text-gray-900">Subtotal</span>
                      <span className="text-lg font-semibold text-gray-900">
                        {formatCurrency(
                          items.reduce((total, item) => {
                            const productTotal = (item.price_at_time || item.product?.price || 0) * item.quantity;
                            return total + productTotal;
                          }, 0)
                        )}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1 text-right">
                      Delivery charges will be added at checkout
                    </p>
                  </div>
                </div>

                <Button 
                  onClick={handleCheckout}
                  className="w-full mt-6"
                  disabled={isLoading}
                >
                  Proceed to Checkout
                </Button>

                <Button 
                  variant="outline"
                  onClick={() => window.location.href = '/products'}
                  className="w-full mt-2"
                >
                  Continue Shopping
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Wrap component in error boundary-like safety
const CartPageWithErrorHandling: React.FC = () => {
  try {
    return <CartPage />;
  } catch (error) {
    console.error('CartPage error:', error);
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-red-500 mb-4">
            <ExclamationTriangleIcon className="h-16 w-16 mx-auto" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Something went wrong</h2>
          <p className="text-gray-600 mb-6">There was an error loading the cart page. Please try refreshing.</p>
          <Button onClick={() => window.location.reload()}>
            Refresh Page
          </Button>
        </div>
      </div>
    );
  }
};

export default CartPageWithErrorHandling;
